import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { useActuatorRegistration } from './use-actuator-registration';

// Brings the top of the wrapped component into focus
export const FocusableActuator = (WrappedComponent) => {
  const HOC = ({ actuatorProps, actuationProps, ...restProps }) => {
    const needsActivation = useCallback(queryPhase => queryPhase === 'focus', []);
    const [isActive, setIsActive] = useState(false);
    const activate = useCallback((phase) => {
      if (phase === 'focus') {
        setIsActive(true);
      }
    }, []);

    const { actuatorId, debugMode } = useActuatorRegistration({ activate, needsActivation, actuatorProps });

    const focusElementRef = useRef();

    useEffect(() => {
      if (isActive) {
        const focusables = focusElementRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusables.length > 0) {
          focusables[0].focus();
        } else {
          focusElementRef.current.scrollIntoView({
            behavior: 'smooth',
          });
        }
        setIsActive(false);
      }
    }, [isActive]);

    return (
      <>
        <div ref={focusElementRef} style={{ position: 'relative', display: 'block' }}>
          {debugMode && `Foc ID: ${actuatorId}`}
          <WrappedComponent
            {...restProps}
            {...WrappedComponent.isActuator ? { actuatorProps, actuationProps } : {}}
          />
        </div>
      </>
    );
  };

  HOC.propTypes = {
    actuatorProps: PropTypes.shape({
      registerActuator: PropTypes.func.isRequired,
      unregisterActuator: PropTypes.func.isRequired,
      debugMode: PropTypes.bool,
    }),
    actuationProps: PropTypes.shape({}),
  };

  HOC.defaultProps = {
    actuatorProps: {
      debugMode: false,
    },
    actuationProps: undefined,
  };

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `FocusableActuator(${getDisplayName(WrappedComponent)})`;
  HOC.isActuator = true;

  return HOC;
};
